import { useEffect } from "react";
import { PortfolioItem } from "@/pages/Portfolio";
import { Share2, X } from "lucide-react";
import { PortfolioDataItem } from "@/types/types";
import {
  getPortfolioMediaPath,
  sharePortfolioPath,
} from "@/lib/portfolioLinks";

interface CreativeLightboxProps {
  item: PortfolioItem;
  group: PortfolioDataItem;
  onClose: () => void;
}

const CreativeLightbox: React.FC<CreativeLightboxProps> = ({
  item,
  group,
  onClose,
}) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[120] grid place-items-center bg-[#050b10]/95 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
    >
      <button
        type="button"
        onClick={() =>
          sharePortfolioPath(
            `${item.brand} creative ${item.mediaIndex + 1}`,
            getPortfolioMediaPath(group, item.mediaIndex),
          )
        }
        className="absolute pr-0.5 right-16 top-4 grid h-11 w-11 place-items-center rounded-full bg-[#f5f5f5] text-[#0d1821] outline-none ring-[#00a878] transition hover:bg-[#c3f620] focus-visible:ring-2"
        aria-label="Share creative"
      >
        <Share2 className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-[#f5f5f5] text-[#0d1821] outline-none ring-[#00a878] transition hover:bg-[#c3f620] focus-visible:ring-2"
        aria-label="Close preview"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="grid max-h-[92svh] max-w-6xl gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-center">
        <div className="min-h-0 overflow-hidden rounded-lg bg-transparent">
          <img
            src={item.src}
            alt={item.title}
            className="max-h-[72svh] w-full object-contain lg:max-h-[90svh]"
          />
        </div>
        <aside className="rounded-lg border border-[#f5f5f5]/10 bg-[#0d1821] p-5 font-poppins">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#00a878]">
            Creative {item.mediaIndex + 1}
          </p>
          {item.brand && (
            <p className="mt-4 font-orbitron text-2xl font-medium uppercase tracking-[1px]">
              {item.brand}
            </p>
          )}
          <h2 className="mt-3 text-sm font-medium text-[#c3f620]">
            {item.title}
          </h2>
          <p className="mt-3 text-sm leading-7 text-[#f5f5f5]/72">
            {item.description}
          </p>
        </aside>
      </div>
    </div>
  );
};

export default CreativeLightbox;
